import { useCallback, useMemo, useState, Dispatch, SetStateAction, useEffect } from 'react'
import { CreatorProfileInstagram, HubCoreService, LogService, ActivateProfileHelper } from '@/hub-shared'
import { onModalCreatorsInsightsObservable } from '@/observables/on-modal-creators-insights.observable'

interface UseCreatorsInsightsProps {
  user: { squidId: string } | null
}

export const useCreatorsInsights = ({ user }: UseCreatorsInsightsProps) => {
  const hubCoreService = useMemo(() => new HubCoreService(), [])
  const logService = useMemo(() => new LogService(), [])
  const activateProfileHelper = useMemo(() => new ActivateProfileHelper(), [])
  const [loadingToken, setLoadingToken] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false)
  const [profiles, setProfiles] = useState<CreatorProfileInstagram[]>([])
  const [profilesLoading, setProfilesLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const PAGE_SIZE = 10
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loadingPagination, setLoadingPagination] = useState<boolean>(false)

  useEffect(() => {
    const onModalCreatorsInsightsSubscription = onModalCreatorsInsightsObservable?.subscribe((open) => {
      setIsModalOpen(open)
    })

    return () => onModalCreatorsInsightsSubscription?.unsubscribe()
  }, [])

  useEffect(() => {
    if (user?.squidId) {
      fetchProfiles()
    }
  }, [user?.squidId])

  const handleButtonCreatorsInsightsClick = useCallback(
    async (profiles: CreatorProfileInstagram[]) => {
      if (loadingToken || !user?.squidId) return

      const hasInsights = profiles.some((profile) => profile.hasCreatorsInsights)

      if (hasInsights) {
        try {
          setLoadingToken(true)
          const response = await hubCoreService.generateToken(user.squidId)

          if (response?.link) {
            window.location.href = `${response.link}&origin=${window.location.origin}`
          }
        } catch (error) {
          logService.error({ message: 'Error on generate token', error })
        } finally {
          setLoadingToken(false)
        }
      } else {
        onModalCreatorsInsightsObservable.next(true)
      }
    },
    [user?.squidId, loadingToken, hubCoreService, logService, onModalCreatorsInsightsObservable]
  )

  const handleConfirm = async () => {
    setLoadingToken(true)

    try {
      if (Array.isArray(profiles) && profiles.length === 0) {
        const tokenResponse = await hubCoreService.generateToken(user?.squidId || '')
        if (tokenResponse && tokenResponse.link) {
          window.location.href = `${tokenResponse.link}&origin=${window.location.origin}`
        }
      } else {
        setIsActivationModalOpen(true)
      }
    } catch (error) {
      logService.error({ message: 'Error on generate token', error })
    } finally {
      setIsModalOpen(false)
      setLoadingToken(false)
    }
  }

  const handleToggleActivation = useCallback(
    async (profileId: string, socialNetwork: 'instagram' | 'tiktok' | 'youtube' | 'twitter', currentState: boolean) => {
      try {
        await activateProfileHelper?.activateProfileForCreatorsInsights({ profileId, currentState, socialNetwork })
        setProfiles((profiles) =>
          profiles.map((profile) => (profile.profileId === profileId ? { ...profile, ...{ hasCreatorsInsights: !currentState } } : profile))
        )
      } catch (error: any) {
        logService.error({ message: 'Error on toggle creator insights', error })
      }
    },
    [activateProfileHelper, logService]
  )

  const handleTogglePublicProfile = useCallback(
    async (profileId: string, socialNetwork: 'instagram' | 'tiktok' | 'youtube' | 'twitter', currentState: boolean) => {
      try {
        await activateProfileHelper?.activateProfileForPublicView({ profileId, currentState, socialNetwork })
        setProfiles((profiles) =>
          profiles.map((profile) =>
            profile.profileId === profileId ? { ...profile, ...{ isSharedCreatorsInsights: !currentState } } : profile
          )
        )
      } catch (error: any) {
        logService.error({ message: 'Error on toggle creator insights', error })
      }
    },
    [activateProfileHelper, logService]
  )

  const fetchAllProfiles = useCallback(async () => {
    if (!user?.squidId) return

    setProfilesLoading(true)
    setError(null)

    try {
      let page = 1
      let allProfiles: CreatorProfileInstagram[] = []
      let hasMore = true

      while (hasMore) {
        const response = await hubCoreService.getCreatorProfileBySquidId(user.squidId, false, page, 10)

        if (!response?.length) {
          hasMore = false
        } else {
          allProfiles = [...allProfiles, ...response]
          page++
        }
      }

      setProfiles(allProfiles)

      return allProfiles
    } catch (err) {
      logService.error({ message: 'Error on get creator profile', error })
    } finally {
      setProfilesLoading(false)
    }
  }, [user?.squidId])

  const handleCreatorsInsightsClick = async () => {
    const allProfiles = (await fetchAllProfiles()) ?? []

    handleButtonCreatorsInsightsClick(allProfiles)
  }

  const fetchProfiles = useCallback(async () => {
    if (loadingPagination || !hasMore) return

    setLoadingPagination(true)

    try {
      hubCoreService.getCreatorProfileBySquidId(user?.squidId || '', false, page, PAGE_SIZE).then((response) => {
        if (response?.length) {
          setProfiles((old) => [...old, ...(response as CreatorProfileInstagram[])])
          setPage(page + 1)
        } else {
          setHasMore(false)
        }
      })
    } catch (error) {
      logService.error({ message: 'Error on get creator profile', error })
    } finally {
      setLoadingPagination(false)
    }
  }, [loadingPagination, hasMore, page, user?.squidId])

  return {
    loadingToken,
    isModalOpen,
    setIsModalOpen,
    handleButtonCreatorsInsightsClick,
    handleConfirm,
    isActivationModalOpen,
    setIsActivationModalOpen,
    handleToggleActivation,
    handleTogglePublicProfile,
    profiles,
    profilesLoading,
    error,
    fetchAllProfiles,
    setProfiles,
    handleCreatorsInsightsClick,
    fetchProfiles,
    page,
    hasMore,
    loadingPagination,
  }
}
